import { useInView } from '@hooks/useInView';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { LazyImageStyles as S } from './LazyImage.styles';

export interface LazyImageProps {
  src?: string | null;
  alt?: string | null;
  width: number;
  height: number;
  disableBlur?: boolean;
  preload?: boolean;
}

const LazyImage: FunctionComponent<LazyImageProps> = (props) => {
  const [imageRef, inView] = useInView<HTMLImageElement>({ threshold: 0 });
  const [lowResLoaded, setLowResLoaded] = useState<boolean>(false);
  const [highResLoaded, setHighResLoaded] = useState<boolean>(false);

  if (!props.src) {
    return null;
  }

  const handleLowResLoaded = (): void => {
    setLowResLoaded(true);
  };

  useEffect(() => {
    // This useeffect means the page has loaded but the image has already loaded before any react hydration runs
    // which means none of the onload event handlers were working
    if (imageRef.current && imageRef.current.complete) {
      setLowResLoaded(true);
    }
  }, []);

  const handleHighResLoaded = (): void => {
    setHighResLoaded(true);
  };

  const combiner = props.src.indexOf('?') > 0 ? '&' : '?';
  const heightRatio = props.height / props.width;

  const lowResSrc = `${
    props.src
  }${combiner}width=${64}&heightratio=${heightRatio}&mode=crop&blur=70&sigma=1.5&threshold=10`;
  const highResSrc = `${props.src}${combiner}width=${props.width}&heightratio=${heightRatio}&mode=crop`;

  return (
    <>
      {props.preload ? (
        <Helmet>
          <link rel="preload" href={lowResSrc} as="image" />
        </Helmet>
      ) : null}

      {lowResLoaded ? (
        <S.Image
          ref={imageRef}
          src={highResSrc}
          loading="lazy"
          onLoad={handleHighResLoaded}
          aspectRatio={`${props.width} / ${props.height}`}
          blur={!highResLoaded}
          alt={props.alt ? props.alt : undefined}
          role={props.alt ? undefined : 'presentation'}
        />
      ) : (
        <S.Image
          ref={imageRef}
          src={lowResSrc}
          loading="lazy"
          onLoad={handleLowResLoaded}
          aspectRatio={`${props.width} / ${props.height}`}
          blur={!inView}
          alt={props.alt ? props.alt : undefined}
          role={props.alt ? undefined : 'presentation'}
        />
      )}
    </>
  );
};

export default LazyImage;
